import { Profile, RoleMatrix } from '../../../global-state/types';
import { UserPermissionType } from '../../../hooks/useUserPermissions';
import { Icons } from '../../../stories/atoms';
import { permissionsTranslations } from '../../../translations';
import { getSuamRoles, isSupportedSuamRole, RoleMapping } from '../../../utils';

export const PERMISSION_ACTIONS = [
  { key: 'canCreate', label: 'Create' },
  { key: 'canRead', label: 'Read' },
  { key: 'canUpdate', label: 'Update' },
  { key: 'canDelete', label: 'Delete' },
] as const;

export const PERMISSION_TYPE_MAP: Record<string, string> = {
  canCreate: 'C',
  canRead: 'R',
  canUpdate: 'U',
  canDelete: 'D',
};

// Check if current user can edit employee permissions based on SUAM role hierarchy
export const canEditEmployeePermissions = (
  employee: Profile | undefined,
  currentUser: Profile,
  currentUserRoles: string[],
  roleMatrix: RoleMatrix,
  currentStoreId: string,
): boolean => {
  if (!employee || employee.id === currentUser.id) {
    return false;
  }

  const targetEmployeeRoles = getSuamRoles(employee, currentStoreId);
  const roleManagement = roleMatrix?.box?.roleManagement;

  if (!roleManagement) {
    return false;
  }

  for (const currentRole of currentUserRoles) {
    const mappedCurrentRole = isSupportedSuamRole(currentRole) ? RoleMapping[currentRole] : 'other';
    const rolePermissions = roleManagement[mappedCurrentRole];

    if (!rolePermissions) continue;

    for (const targetRole of targetEmployeeRoles) {
      const mappedTargetRole = isSupportedSuamRole(targetRole) ? RoleMapping[targetRole] : 'other';
      const permission = rolePermissions[mappedTargetRole];

      // Check if current role can update ('U' or 'RU' or 'CRUD') the target role
      if (permission && permission.includes('U')) {
        return true;
      }
    }
  }

  return false;
};

export type PermissionTableRow = {
  id: UserPermissionType;
  name: string;
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
};

const {
  pagePermissions,
  pagePermissionsDescription,
  widgetPermissions,
  widgetPermissionsDescription,
  settingsPermissions,
  settingsPermissionsDescription,
  pageColumnLabel,
  widgetsColumnLabel,
  settingsColumnLabel,
} = permissionsTranslations;

export type PermissionType = 'pages' | 'widgets' | 'settings';

export interface PermissionConfig {
  type: PermissionType;
  titleKey: string;
  titleDefault: string;
  descriptionKey: string;
  descriptionDefault: string;
  icon: keyof typeof Icons;
  columnLabel: string; // Page/Widget/Settings
}

export const permissionConfigs: Record<string, PermissionConfig> = {
  page_permissions: {
    type: 'pages',
    titleKey: pagePermissions.key,
    titleDefault: pagePermissions.defaultValue,
    descriptionKey: pagePermissionsDescription.key,
    descriptionDefault: pagePermissionsDescription.defaultValue,
    icon: 'LIST',
    columnLabel: pageColumnLabel.defaultValue,
  },
  widget_permissions: {
    type: 'widgets',
    titleKey: widgetPermissions.key,
    titleDefault: widgetPermissions.defaultValue,
    descriptionKey: widgetPermissionsDescription.key,
    descriptionDefault: widgetPermissionsDescription.defaultValue,
    icon: 'ALLWIDGETS',
    columnLabel: widgetsColumnLabel.defaultValue,
  },
  settings_permissions: {
    type: 'settings',
    titleKey: settingsPermissions.key,
    titleDefault: settingsPermissions.defaultValue,
    descriptionKey: settingsPermissionsDescription.key,
    descriptionDefault: settingsPermissionsDescription.defaultValue,
    icon: 'SETTINGS',
    columnLabel: settingsColumnLabel.defaultValue,
  },
};

export const getEmployeePermissions = (
  permissionType: string,
  employee: Profile | undefined,
  roleMatrix: RoleMatrix,
  currentStoreId: string,
) => {
  if (!employee) return null;

  const employeeRoles = getSuamRoles(employee, currentStoreId);
  const clientKey = 'box';

  const basePermissions = employeeRoles
    .map((role) => {
      let mappedRole = isSupportedSuamRole(role) ? RoleMapping[role] : 'other';
      if (mappedRole === RoleMapping.EV) mappedRole = 'other';
      return roleMatrix?.[clientKey]?.featurePermissions?.[permissionType]?.permissions?.[mappedRole] || '';
    })
    .join('');

  const additionalPermissions =
    employee.additionalPermissions?.[currentStoreId]?.[permissionType]?.permissionType || '';

  const allPermissions = basePermissions + additionalPermissions;

  return PERMISSION_ACTIONS.reduce((acc, { key }) => {
    const type = PERMISSION_TYPE_MAP[key];
    acc[key] = allPermissions.includes(type);
    return acc;
  }, {} as Record<'canCreate' | 'canRead' | 'canUpdate' | 'canDelete', boolean>);
};

const DISABLED_PERMISSIONS_FOR_EDIT: readonly UserPermissionType[] = [
  // Pages
  'areaOverview',
  'areaResultPage',
  // Overview widgets
  'conversionRateOverview',
  'membersOverview',
  'salesOverview',
  'efficiencyOverview',
  'rfidOverview',
  'queueAndCustomerOverview',
  'budgetAndPlanOverview',
  'kpiTargetsOverview',
  'getCountsOverview',
  // Settings
  'areaCaps',
  'lockTargets',
  'currencyConverter',
];

export const isEditPermissionDisabled = (permissionId: UserPermissionType): boolean => {
  return DISABLED_PERMISSIONS_FOR_EDIT.includes(permissionId);
};
